import { generateId, useSite } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { SiteValuesBuildingProfileData } from "@inrange/building-manager-api-client/models-site";
import { formatDateTimeFromSeconds } from "@inrange/theme-components";
import { useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import getCustomerAppDomain from "../../utils/getCustomerAppDomain";
import FormInputPercentageNoValidation from "../FormInputPercentageNoValidation";
import Loading from "../Loading";
import { orgTypeMap } from "./org-constants";

const LandlordSettings: React.FC<{
  settings: Record<string, any>;
  updateSettings: (updates: Record<string, any>) => void;
  buildingProfiles: Record<string, SiteValuesBuildingProfileData>;
}> = ({ settings, updateSettings, buildingProfiles }) => {
  return (
    <>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="maxNetworkImportTariff">
          <Form.Label>Default building profile</Form.Label>
          <Form.Select
            value={settings.buildingProfile ?? ""}
            onChange={(e) => {
              updateSettings({
                buildingProfile: e.target.value,
                maxNetworkImportTariff:
                  buildingProfiles[e.target.value]?.maxNetworkImportTariff ??
                  "",
                maxTenantTariff:
                  buildingProfiles[e.target.value]?.maxTenantTariff ?? "",
                defaultMarketTariff:
                  buildingProfiles[e.target.value]?.defaultMarketTariff ?? "",
              });
            }}
          >
            <option value="">Select profile</option>
            {Object.entries(buildingProfiles).map(
              ([profileValue, profileData]: [string, any]) => (
                <option key={profileValue} value={profileValue}>
                  {profileData.name}
                </option>
              )
            )}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="minIRR">
          <Form.Label>Minimum target IRR</Form.Label>
          <FormInputPercentageNoValidation
            value={settings.minIRR}
            onChange={(e) => {
              updateSettings({ minIRR: e.target.value });
            }}
          />
        </Form.Group>
      </Col>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="defaultInvestmentModel">
          <Form.Label>Default investment model</Form.Label>
          <Form.Select
            value={settings.defaultInvestmentModel ?? ""}
            onChange={(e) => {
              updateSettings({ defaultInvestmentModel: e.target.value });
            }}
          >
            <option value="">No default</option>
            <option value="license">License</option>
            <option value="lease">Lease</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );
};

const TenantSettings: React.FC<{
  settings: Record<string, any>;
  updateSettings: (updates: Record<string, any>) => void;
  buildingProfiles: Record<string, SiteValuesBuildingProfileData>;
}> = ({ settings, updateSettings, buildingProfiles }) => {
  return (
    <>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="buildingProfile">
          <Form.Label>Default building profile</Form.Label>
          <Form.Select
            value={settings.buildingProfile ?? ""}
            onChange={(e) => {
              updateSettings({
                buildingProfile: e.target.value,
                maxNetworkImportTariff:
                  buildingProfiles[e.target.value]?.maxNetworkImportTariff ??
                  "",
                maxTenantTariff:
                  buildingProfiles[e.target.value]?.maxTenantTariff ?? "",
                defaultMarketTariff:
                  buildingProfiles[e.target.value]?.defaultMarketTariff ?? "",
              });
            }}
          >
            <option value="">Select profile</option>
            {Object.entries(buildingProfiles).map(
              ([profileValue, profileData]: [string, any]) => (
                <option key={profileValue} value={profileValue}>
                  {profileData.name}
                </option>
              )
            )}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="maxTenantTariff">
          <Form.Label>Max on-site tariff</Form.Label>
          <Form.Control
            type="text"
            value={settings.maxTenantTariff}
            onChange={(e) => {
              updateSettings({ maxTenantTariff: e.target.value });
            }}
          />
        </Form.Group>
      </Col>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="maxNetworkImportTariff">
          <Form.Label>Max import tariff</Form.Label>
          <Form.Control
            type="text"
            value={settings.maxNetworkImportTariff}
            onChange={(e) => {
              updateSettings({ maxNetworkImportTariff: e.target.value });
            }}
          />
        </Form.Group>
      </Col>
      <Col sm={3}>
        <Form.Group className="mb-3" controlId="defaultMarketTariff">
          <Form.Label>Default market tariff</Form.Label>
          <Form.Control
            type="text"
            value={settings.defaultMarketTariff}
            onChange={(e) => {
              updateSettings({ defaultMarketTariff: e.target.value });
            }}
          />
        </Form.Group>
      </Col>
    </>
  );
};

const EPCSettings: React.FC<{
  settings: Record<string, any>;
  updateSettings: (updates: Record<string, any>) => void;
}> = ({ settings, updateSettings }) => {
  return (
    <Col sm={6}>
      <Form.Group className="mb-3" controlId="defaultOAndMEmail">
        <Form.Label>Default O&M contact email</Form.Label>
        <Form.Control
          type="email"
          value={settings.defaultOAndMEmail}
          onChange={(e) => {
            updateSettings({ defaultOAndMEmail: e.target.value });
          }}
          placeholder="e.g. support@epc.com"
        />
      </Form.Group>
    </Col>
  );
};

const OrgSettingsForm = ({
  organisation,
  handleSubmit,
  isDisableSubmit,
}: {
  organisation?: Organisation;
  handleSubmit: (payload: any) => void;
  isDisableSubmit: boolean;
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [orgName, setOrgName] = useState(organisation?.name ?? "");
  const [orgType, setOrgType] = useState(
    `${organisation?.isTest ? "Test " : ""}${organisation?.orgType ?? "Tenant"}`
  );
  const [settings, setSettings] = useState({
    buildingProfile: organisation?.buildingProfile ?? "",
    maxTenantTariff: organisation?.maxTenantTariff ?? "",
    maxNetworkImportTariff: organisation?.maxNetworkImportTariff ?? "",
    defaultMarketTariff: organisation?.defaultMarketTariff ?? "",
    minIRR: organisation?.minIRR ?? "",
    defaultInvestmentModel: organisation?.defaultInvestmentModel ?? "",
    defaultOAndMEmail: organisation?.defaultOAndMEmail ?? "",
    sdmExcludeFromMatching: organisation?.sdmExcludeFromMatching ?? false,
    demo: organisation?.demo ?? false,
  });

  const updateSettings = (updates: Record<string, any>) => {
    setHasUnsavedChanges(true);
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  const { fetchSiteValues } = useSite({
    enableNonEssentialQueries: true,
    app: "admin",
  });

  const params = new URLSearchParams(window.location.search);
  const testCreateTemporaryOrg =
    params.get("testCreateTemporaryOrg") === "true";

  const handleFormSubmit = () => {
    const payload = {
      ...(organisation === undefined && { id: generateId() }),
      name: orgName.trim(),
      buildingProfile: settings.buildingProfile,
      ...orgTypeMap[orgType],
      ...(orgTypeMap[orgType].orgType === "Tenant" && {
        maxTenantTariff: settings.maxTenantTariff,
        maxNetworkImportTariff: settings.maxNetworkImportTariff,
        defaultMarketTariff: settings.defaultMarketTariff,
      }),
      ...(orgTypeMap[orgType].orgType === "Landlord" && {
        minIRR: settings.minIRR,
        defaultInvestmentModel: settings.defaultInvestmentModel,
      }),
      ...(orgTypeMap[orgType].orgType === "EPC" && {
        defaultOAndMEmail: settings.defaultOAndMEmail,
      }),
      sdmExcludeFromMatching: settings.sdmExcludeFromMatching,
      demo: settings.demo,
      isTemporary: testCreateTemporaryOrg,
    };
    handleSubmit(payload);
    setHasUnsavedChanges(false);
  };

  const disableSubmit = isDisableSubmit || orgName.length < 1;

  if (fetchSiteValues.isError) {
    return <div>Error loading site values.</div>;
  }

  if (fetchSiteValues.isLoading) {
    return <Loading label="Loading site values data..." />;
  }

  const currentOrgType = orgTypeMap[orgType]?.orgType;
  const buildingProfiles = fetchSiteValues.data!.options.buildingProfiles;

  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  const updateAtStr = testMode
    ? "fixed date for test"
    : formatDateTimeFromSeconds(organisation?.updatedAt);

  const portfolioLinkBlock = organisation && currentOrgType !== "EPC" && (
    <Col
      sm={3}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        flexDirection: "column",
      }}
    >
      <a
        href={`${getCustomerAppDomain()}/org/${organisation?.id}/dashboard`}
        target="_blank"
        rel="noreferrer"
      >
        <Button size="lg" variant="success">
          Public portfolio
        </Button>
      </a>
      <div className="text-muted mb-3">Updated: {updateAtStr}</div>
    </Col>
  );

  return (
    <>
      <Row className="mt-3">
        <Col sm={organisation ? 9 : 12}>
          <div className="mb-3">
            <h1>{organisation?.name}</h1>
          </div>
          <Row className="d-flex flex-wrap">
            <Col sm={6}>
              <Form.Group className="mb-3" controlId="orgName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={orgName}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setOrgName(e.target.value);
                  }}
                />
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-3" controlId="orgType">
                <Form.Label>Organisation type</Form.Label>
                <Form.Select
                  value={orgType}
                  onChange={(e) => {
                    setHasUnsavedChanges(true);
                    setOrgType(e.target.value);
                  }}
                >
                  <option hidden disabled value="">
                    Select Type
                  </option>
                  {Object.keys(orgTypeMap).map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {currentOrgType === "Landlord" && (
              <LandlordSettings
                settings={settings}
                updateSettings={updateSettings}
                buildingProfiles={buildingProfiles}
              />
            )}
            {currentOrgType === "Tenant" && (
              <TenantSettings
                settings={settings}
                updateSettings={updateSettings}
                buildingProfiles={buildingProfiles}
              />
            )}
            {currentOrgType === "EPC" && (
              <EPCSettings
                settings={settings}
                updateSettings={updateSettings}
              />
            )}
            <Col sm={2}>
              <div style={{ display: "flex", gap: "24px" }}>
                <Button
                  variant="primary"
                  onClick={handleFormSubmit}
                  style={{ width: "108px", marginTop: 32 }}
                  disabled={disableSubmit || !hasUnsavedChanges}
                >
                  {organisation === undefined ? "Create" : "Update"}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Form.Group
                className="mb-3"
                controlId="sdmExcludeFromMatching"
              >
                <Form.Check
                  type="checkbox"
                  label="Don't SDM match to sites in this org"
                  checked={settings.sdmExcludeFromMatching}
                  onChange={(e) => {
                    updateSettings({
                      sdmExcludeFromMatching: e.target.checked,
                    });
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="demo">
                <Form.Check
                  type="checkbox"
                  label="Enable demo features"
                  checked={settings.demo}
                  onChange={(e) => {
                    updateSettings({ demo: e.target.checked });
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
        {portfolioLinkBlock}
      </Row>
    </>
  );
};

export default OrgSettingsForm;
